export class DataObfuscator {
  private static readonly XOR_KEY = new Uint8Array([
    0x4d, 0x79, 0x48, 0x6f, 0x75, 0x73, 0x65, 0x45, 0x43, 0x53, 0x47, 0x61,
    0x6d, 0x65, 0x53, 0x61, 0x76, 0x65, 0x44, 0x61, 0x74, 0x61, 0x4b, 0x65,
    0x79, 0x32, 0x30, 0x32, 0x35, 0x56, 0x65, 0x72, 0x73, 0x69, 0x6f, 0x6e,
    0x31, 0x2e, 0x30, 0x2e, 0x30, 0x53, 0x65, 0x63, 0x75, 0x72, 0x69, 0x74,
    0x79, 0x4c, 0x61, 0x79, 0x65, 0x72, 0x50, 0x72, 0x6f, 0x74, 0x65, 0x63,
    0x74, 0x69, 0x6f, 0x6e, 0x4f, 0x62, 0x66, 0x75, 0x73, 0x63, 0x61, 0x74,
    0x69, 0x6f, 0x6e, 0x58, 0x4f, 0x52, 0x45, 0x6e, 0x63, 0x72, 0x79, 0x70,
    0x74, 0x69, 0x6f, 0x6e, 0x41, 0x6c, 0x67, 0x6f, 0x72, 0x69, 0x74, 0x68,
    0x6d, 0x43, 0x68, 0x61, 0x72, 0x61, 0x63, 0x74, 0x65, 0x72, 0x53, 0x68,
    0x75, 0x66, 0x66, 0x6c, 0x65, 0x4d, 0x65, 0x74, 0x68, 0x6f, 0x64, 0x44,
    0x61, 0x74, 0x61, 0x49, 0x6e, 0x74, 0x65, 0x67, 0x72, 0x69, 0x74, 0x79,
    0x43, 0x68, 0x65, 0x63, 0x6b, 0x73, 0x75, 0x6d, 0x56, 0x61, 0x6c, 0x69,
    0x64, 0x61, 0x74, 0x69, 0x6f, 0x6e, 0x42, 0x79, 0x74, 0x65, 0x41, 0x72,
    0x72, 0x61, 0x79, 0x50, 0x72, 0x6f, 0x63, 0x65, 0x73, 0x73, 0x69, 0x6e,
    0x67, 0x55, 0x6e, 0x69, 0x74, 0x38, 0x41, 0x72, 0x72, 0x61, 0x79, 0x4d,
    0x61, 0x6e, 0x69, 0x70, 0x75, 0x6c, 0x61, 0x74, 0x69, 0x6f, 0x6e, 0x43,
    0x6f, 0x6d, 0x62, 0x69, 0x6e, 0x61, 0x74, 0x69, 0x6f, 0x6e, 0x4c, 0x61,
    0x79, 0x65, 0x72, 0x53, 0x65, 0x63, 0x75, 0x72, 0x69, 0x74, 0x79, 0x4d,
    0x65, 0x61, 0x73, 0x75, 0x72, 0x65, 0x50, 0x72, 0x65, 0x76, 0x65, 0x6e,
    0x74, 0x69, 0x6f, 0x6e, 0x4c, 0x65, 0x61, 0x6b, 0x61, 0x67, 0x65, 0x43,
    0x6c, 0x69, 0x65, 0x6e, 0x74, 0x53, 0x69, 0x64, 0x65, 0x50, 0x72, 0x6f,
    0x74, 0x65, 0x63, 0x74,
  ]);

  private static readonly OBFUSCATION_PREFIX = "mhecs_v2_";
  private static readonly SHUFFLE_SEED = 0x5a5a5a5a;

  static obfuscate(data: string): string {
    try {
      const binaryData = Uint8Array.from(atob(data), (c) => c.charCodeAt(0));

      const xorEncrypted = this.xorEncrypt(binaryData);

      const base64Encrypted = btoa(String.fromCharCode(...xorEncrypted));

      const shuffled = this.shuffleString(base64Encrypted);

      return this.OBFUSCATION_PREFIX + shuffled;
    } catch (error) {
      console.error("Obfuscation failed:", error);
      throw new Error("Failed to obfuscate data");
    }
  }

  static deobfuscate(obfuscatedData: string): string {
    try {
      if (!obfuscatedData.startsWith(this.OBFUSCATION_PREFIX)) {
        throw new Error("Invalid obfuscated data format");
      }

      const withoutPrefix = obfuscatedData.slice(
        this.OBFUSCATION_PREFIX.length
      );

      const unshuffled = this.unshuffleString(withoutPrefix);

      const encryptedBinary = Uint8Array.from(atob(unshuffled), (c) =>
        c.charCodeAt(0)
      );

      const decrypted = this.xorDecrypt(encryptedBinary);

      return btoa(String.fromCharCode(...decrypted));
    } catch (error) {
      console.error("Deobfuscation failed:", error);
      throw new Error("Failed to deobfuscate data");
    }
  }

  private static xorEncrypt(data: Uint8Array): Uint8Array {
    const result = new Uint8Array(data.length);
    for (let i = 0; i < data.length; i++) {
      result[i] = data[i] ^ this.XOR_KEY[i % this.XOR_KEY.length];
    }
    return result;
  }

  private static xorDecrypt(encryptedData: Uint8Array): Uint8Array {
    return this.xorEncrypt(encryptedData);
  }

  private static shuffleString(input: string): string {
    const chars = input.split("");
    const length = chars.length;

    let seed = this.SHUFFLE_SEED;
    for (let i = length - 1; i > 0; i--) {
      seed = (seed * 1103515245 + 12345) & 0x7fffffff;
      const j = seed % (i + 1);
      [chars[i], chars[j]] = [chars[j], chars[i]];
    }

    return chars.join("");
  }

  private static unshuffleString(shuffled: string): string {
    const chars = shuffled.split("");
    const length = chars.length;
    const swaps: Array<[number, number]> = [];

    let seed = this.SHUFFLE_SEED;
    for (let i = length - 1; i > 0; i--) {
      seed = (seed * 1103515245 + 12345) & 0x7fffffff;
      const j = seed % (i + 1);
      swaps.push([i, j]);
    }

    for (let k = swaps.length - 1; k >= 0; k--) {
      const swap = swaps[k];
      if (swap) {
        const [i, j] = swap;
        [chars[i], chars[j]] = [chars[j], chars[i]];
      }
    }

    return chars.join("");
  }

  static isObfuscated(data: string): boolean {
    return data.startsWith(this.OBFUSCATION_PREFIX);
  }

  static validateIntegrity(data: string): boolean {
    try {
      if (!this.isObfuscated(data)) {
        return false;
      }
      this.deobfuscate(data);
      return true;
    } catch {
      return false;
    }
  }
}
